import React, { useCallback } from 'react';
import { useLocation } from 'react-router';
import queryString from 'query-string-for-all';
import { NumberPagination } from 'src/components/number-pagination';
import { getSortedQuery } from '@prisjakt/product-collection-urls';
import { trackProductCollectionPagination } from '../../tracking';
import type { PageInfo, SearchPageInfo } from 'src/types';

type Props = {
  pageInfo: PageInfo | SearchPageInfo;
  total: number;
  alwaysShowStepButtons?: false;
  onClick?: (clickedOffset?: number) => void;
};

const Pagination = ({
  pageInfo,
  total,
  alwaysShowStepButtons,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onClick = () => {},
}: Props) => {
  const { offset, limit } = pageInfo;
  const { search } = useLocation();
  const linkToPage = useCallback(
    (pageNumber: number) =>
      typeof limit === 'number'
        ? `?${queryString.stringify(
            getSortedQuery({
              ...queryString.parse(search.substr(1)),
              offset: pageNumber > 1 ? (pageNumber - 1) * limit : undefined,
            })
          )}`
        : '',
    [limit, search]
  );
  const currentPage =
    typeof offset === 'number' && typeof limit === 'number'
      ? Math.floor(offset / limit) + 1
      : 1;
  const onClickHandler = useCallback(
    ({ pageNumber, type }: { pageNumber: number; type: string }) => {
      const clickedOffset =
        limit && pageNumber > 1 ? (pageNumber - 1) * limit : undefined;
      onClick(clickedOffset);
      trackProductCollectionPagination(currentPage, pageNumber, type);
    },
    [currentPage, onClick]
  );
  if (typeof limit !== 'number' || typeof offset !== 'number' || limit === 0) {
    return null;
  }

  return (
    <NumberPagination
      currentPage={currentPage}
      onClick={onClickHandler}
      totalPages={Math.ceil(total / limit)}
      linkToPage={linkToPage}
      alwaysShowStepButtons={alwaysShowStepButtons}
    />
  );
};

export default Pagination;
